/**
 * InvoiceManagement.js — Admin only
 *
 * Flow (from task.md PDF-1):
 *   PDD Approved → Invoice Required?
 *     YES → Enter Invoice Number, Invoice Date, Invoice Amount here
 *     NO  → Goes directly to Revenue Management
 *
 * This page shows all LeadRevenue records where:
 *   - invoice_required = true
 *   - invoice_number is blank (not yet entered)
 * Admin enters invoice details inline and saves.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiFileText, FiSearch, FiRefreshCw, FiEye,
  FiEdit2, FiCheck, FiX, FiSave, FiCalendar,
  FiDollarSign, FiAlertCircle,
} from 'react-icons/fi';
import * as api from '@crm/shared/api';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';

/* ─── Colors ─── */
const C = {
  navy:'#0D3B5E', navyL:'#EBF4FF',
  gold:'#F5A623', goldL:'#FEF3DC',
  green:'#16A34A', greenL:'#DCFCE7', greenB:'#86EFAC',
  red:'#DC2626',  redL:'#FEF2F2',  redB:'#FECACA',
  purple:'#7C3AED', purpleL:'#EDE9FE',
  border:'#E5E7EB', borderL:'#F3F4F6', bg:'#F4F6F9',
  white:'#FFFFFF', t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF',
};

const fmtDate = (s) => {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
  } catch { return s; }
};
const fmtAmt = n => n ? '₹' + Number(n).toLocaleString('en-IN') : '—';
const todayISO = () => new Date().toISOString().split('T')[0];

/* ─── Shimmer ─── */
const Sh = () => (
  <div style={{
    height: 60, borderRadius: 8, background: '#F1F5F9', marginBottom: 8,
    backgroundImage: 'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)',
    backgroundSize: '400px 100%', animation: 'cfShimmer 1.4s infinite',
  }}/>
);
const Spin = () => (
  <div style={{
    width: 13, height: 13, borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff',
    animation: 'cfSpin 0.7s linear infinite', flexShrink: 0,
  }}/>
);

const INPUT = {
  height: 36, border: `1.5px solid ${C.border}`, borderRadius: 8,
  padding: '0 10px', fontSize: 13, color: C.t1, background: '#FAFBFC',
  width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none',
};

/* ════════════════════════════════════════════
   INLINE EDIT ROW
════════════════════════════════════════════ */
function InvoiceRow({ revenue, lead, onSaved, navigate }) {
  const [editing,    setEditing]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [autoPayout, setAutoPayout] = useState(null);
  const [form, setForm] = useState({
    invoice_number: revenue.invoice_number || '',
    invoice_date:   revenue.invoice_date   || todayISO(),
    invoice_amount: revenue.payout_to_company || '',  // payout_to_company IS the invoice amount
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const isDone = !!(revenue.invoice_number);
  const leadId = revenue?.lead;
  const existing = Number(revenue?.payout_to_company || 0);

  useEffect(() => {
    // auto_payout_to_company comes from backend LeadRevenueSerializer.get_lead_detail
    // OR from lead_detail in revenue object
    const autoPct = revenue?.lead_detail?.auto_payout_to_company;
    const dbPayout = Number(revenue?.payout_to_company || 0);

    if (dbPayout > 0) {
      // DB already has value — use it, also pre-fill form
      setAutoPayout(dbPayout);
      setForm(prev => ({ ...prev, invoice_amount: prev.invoice_amount || String(dbPayout) }));
    } else if (autoPct != null && autoPct > 0) {
      // Backend calculated auto value
      setAutoPayout(autoPct);
      setForm(prev => ({ ...prev, invoice_amount: prev.invoice_amount || String(autoPct) }));
    } else {
      setAutoPayout(0);
    }
  }, [leadId, existing]); // eslint-disable-line

  // Display: use DB value if > 0, else use calculated
  const displayPayout = Number(revenue.payout_to_company || 0) > 0
    ? Number(revenue.payout_to_company)
    : autoPayout;

  const handleSave = async () => {
    if (!form.invoice_number.trim()) { toast.error('Invoice number is required'); return; }
    if (!form.invoice_date)          { toast.error('Invoice date is required');   return; }
    setSaving(true);
    try {
      await api.updateInvoice({
        lead_id:        revenue.lead,
        revenue_id:     revenue.id,
        invoice_number: form.invoice_number.trim(),
        invoice_date:   form.invoice_date,
        invoice_amount: form.invoice_amount ? parseFloat(form.invoice_amount) : null,
      });
      toast.success('Invoice details saved!');
      setEditing(false);
      onSaved();
      if (window.confirm('Invoice saved. Go to Revenue Management?')) {
        navigate('/revenue-management');
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to save invoice');
    } finally { setSaving(false); }
  };

  return (
    <tr
      style={{ background: editing ? '#FAFBFC' : C.white, transition: 'background 0.1s' }}
      onMouseEnter={e => { if (!editing) e.currentTarget.style.background = '#F8FAFC'; }}
      onMouseLeave={e => { if (!editing) e.currentTarget.style.background = C.white; }}
    >
      {/* Lead / Customer */}
      <td style={{ padding: '12px 16px', borderBottom: `1px solid ${C.borderL}` }}>
        <div
          onClick={() => navigate(`/leads/${revenue.lead}`)}
          style={{ fontWeight: 700, fontSize: 13.5, color: C.navy,
            cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2, display: 'inline-block' }}>
          {lead?.customer_name || `Lead #${revenue.lead}`}
        </div>
        <div style={{ fontSize: 11.5, color: C.t4, marginTop: 2, fontFamily: 'monospace' }}>
          {lead?.lead_no || `#${revenue.lead}`}
        </div>
      </td>

      {/* Product */}
      <td style={{ padding: '12px 16px', borderBottom: `1px solid ${C.borderL}`, fontSize: 13, color: C.t2 }}>
        {lead?.product?.name || '—'}
      </td>

      {/* Payout to Company — DB value or auto-calculated */}
      <td style={{ padding: '12px 16px', borderBottom: `1px solid ${C.borderL}` }}>
        {displayPayout != null && displayPayout > 0 ? (
          <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
            <span style={{ fontSize:13, fontWeight:700, color:C.navy }}>
              {fmtAmt(displayPayout)}
            </span>
            {Number(revenue.payout_to_company || 0) === 0 && autoPayout != null && (
              <span style={{ fontSize:10, color:C.t4, display:'flex', alignItems:'center', gap:3 }}>
                <span style={{ background:C.goldL, color:'#92400E', padding:'1px 5px',
                  borderRadius:4, fontSize:9.5, fontWeight:700 }}>AUTO</span>
                Disb × Rate × Income%
              </span>
            )}
          </div>
        ) : (
          <span style={{ fontSize:12.5, color:C.t4 }}>{autoPayout === null ? 'Calculating…' : '—'}</span>
        )}
      </td>

      {/* Invoice Number */}
      <td style={{ padding: '12px 16px', borderBottom: `1px solid ${C.borderL}`, minWidth: 160 }}>
        {editing ? (
          <input
            type="text"
            placeholder="INV-2025-001"
            value={form.invoice_number}
            onChange={e => set('invoice_number', e.target.value)}
            style={INPUT}
            autoFocus
          />
        ) : (
          <span style={{
            fontSize: 13, fontWeight: isDone ? 700 : 400,
            color: isDone ? C.t1 : C.t4,
            fontFamily: isDone ? 'monospace' : 'inherit',
          }}>
            {revenue.invoice_number || '— Not entered'}
          </span>
        )}
      </td>

      {/* Invoice Date */}
      <td style={{ padding: '12px 16px', borderBottom: `1px solid ${C.borderL}`, minWidth: 140 }}>
        {editing ? (
          <input
            type="date"
            value={form.invoice_date}
            max={todayISO()}
            onChange={e => set('invoice_date', e.target.value)}
            style={INPUT}
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: revenue.invoice_date ? C.t1 : C.t4 }}>
            {revenue.invoice_date
              ? <><FiCalendar size={12} style={{ color: C.t4 }}/>{fmtDate(revenue.invoice_date)}</>
              : '— Not entered'}
          </div>
        )}
      </td>

      {/* Invoice Amount */}
      <td style={{ padding: '12px 16px', borderBottom: `1px solid ${C.borderL}`, minWidth: 130 }}>
        {editing ? (
          <input
            type="number"
            placeholder="₹ Amount"
            min="0"
            step="0.01"
            value={form.invoice_amount}
            onChange={e => set('invoice_amount', e.target.value)}
            style={INPUT}
          />
        ) : (
          <span style={{ fontSize: 13, fontWeight: revenue.payout_to_company ? 600 : 400, color: revenue.payout_to_company ? C.navy : C.t4 }}>
            {revenue.payout_to_company && Number(revenue.payout_to_company) > 0 ? fmtAmt(revenue.payout_to_company) : '— Not entered'}
          </span>
        )}
      </td>

      {/* Status badge */}
      <td style={{ padding: '12px 16px', borderBottom: `1px solid ${C.borderL}` }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 700,
          background: isDone ? C.greenL : C.goldL,
          color: isDone ? C.green : '#92400E',
          border: `1px solid ${isDone ? C.greenB : '#FDE68A'}`,
        }}>
          {isDone ? <><FiCheck size={10}/> Done</> : '⏳ Pending'}
        </span>
      </td>

      {/* Actions */}
      <td style={{ padding: '12px 16px', borderBottom: `1px solid ${C.borderL}` }}>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          {editing ? (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  height: 32, padding: '0 12px', borderRadius: 8,
                  background: C.navy, color: '#fff', border: 'none',
                  fontSize: 12.5, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                }}>
                {saving ? <Spin/> : <><FiSave size={12}/> Save</>}
              </button>
              <button
                onClick={() => { setEditing(false); setForm({ invoice_number: revenue.invoice_number || '', invoice_date: revenue.invoice_date || todayISO(), invoice_amount: revenue.payout_to_company || '' }); }}
                disabled={saving}
                style={{
                  height: 32, padding: '0 10px', borderRadius: 8,
                  background: C.white, color: C.t2, border: `1px solid ${C.border}`,
                  fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setEditing(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  height: 32, padding: '0 10px', borderRadius: 8,
                  background: C.navyL, color: C.navy, border: `1px solid ${C.border}`,
                  fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                <FiEdit2 size={12}/> {isDone ? 'Edit' : 'Enter'}
              </button>
              <button
                onClick={() => navigate(`/leads/${revenue.lead}`)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  height: 32, padding: '0 10px', borderRadius: 8,
                  background: C.white, color: C.t2, border: `1px solid ${C.border}`,
                  fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                <FiEye size={12}/> Lead
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════ */
export default function InvoiceManagement() {
  const navigate = useNavigate();

  const [records,  setRecords]  = useState([]);
  const [leadsMap, setLeadsMap] = useState({});
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('PENDING');
  const [search,   setSearch]   = useState('');
  const [page,     setPage]     = useState(1);
  const [total,    setTotal]    = useState(0);
  const PAGE = 20;

  const fetchData = useCallback(async (pg = 1, status = 'PENDING', q = '') => {
    setLoading(true);
    try {
      const params = { page: pg, page_size: PAGE, invoice_required: true };
      if (q.trim()) params.search = q.trim();

      // Filter logic:
      // PENDING  → invoice_required=true AND invoice_number is blank
      // DONE     → invoice_required=true AND invoice_number is filled
      // ALL      → invoice_required=true (both)
      if (status === 'PENDING') params.invoice_entered = 'false';
      if (status === 'DONE')    params.invoice_entered = 'true';

      // Use getLeadRevenues with invoice filter params
      const res  = await api.getLeadRevenues(params);
      const data = res?.data;
      const arr  = Array.isArray(data?.results) ? data.results
                 : Array.isArray(data) ? data : [];

      // Client-side filter if backend doesn't support invoice_entered param
      let filtered = arr.filter(r => r.invoice_required === true || r.invoice_required === 'true');
      if (status === 'PENDING') filtered = filtered.filter(r => !r.invoice_number);
      if (status === 'DONE')    filtered = filtered.filter(r =>  !!r.invoice_number);

      setRecords(filtered);
      setTotal(data?.count || filtered.length);
      setPage(pg);

      // Fetch lead details in parallel
      const ids = [...new Set(filtered.map(r => r.lead).filter(Boolean))];
      const map = {};
      await Promise.allSettled(ids.map(async id => {
        try {
          const r = await api.getLeadById(id);
          if (r?.data) map[id] = r.data;
        } catch {}
      }));
      setLeadsMap(prev => ({ ...prev, ...map }));
    } catch (e) {
      toast.error('Failed to load invoice records');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(1, filter, search); }, []); // eslint-disable-line

  const onTabClick = (val) => { setFilter(val); fetchData(1, val, search); };
  const totalPages = Math.ceil(total / PAGE);

  const pendingCount = records.filter(r => !r.invoice_number).length;
  const doneCount    = records.filter(r =>  !!r.invoice_number).length;

  return (
    <>
      <style>{`
        @keyframes cfShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes cfSpin    { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes cfFadeIn  { from{opacity:0;transform:translateY(5px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      <Layout pageTitle="Invoice Management">
        <div style={{ display:'flex', flexDirection:'column', gap:16, animation:'cfFadeIn 0.2s ease both' }}>

          {/* ── HEADER ── */}
          <div style={{
            background: 'linear-gradient(120deg,#6D28D9,#7C3AED)',
            borderRadius: 14, padding: '20px 24px',
            boxShadow: '0 4px 18px rgba(109,40,217,0.25)',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)' }}/>
            <div style={{ fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.55)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>Admin Panel</div>
            <div style={{ fontSize:21, fontWeight:800, color:'#fff', letterSpacing:'-0.02em' }}>Invoice Management</div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.6)', marginTop:4 }}>
              Enter invoice number, date and amount for leads requiring invoice
            </div>
            {/* Summary pills */}
            <div style={{ marginTop:14, display:'flex', gap:10, flexWrap:'wrap' }}>
              {[
                { label: `${loading ? '…' : pendingCount} Pending`, color: '#FDE68A' },
                { label: `${loading ? '…' : doneCount} Done`,       color: '#86EFAC' },
                { label: `${loading ? '…' : total} Total`,          color: 'rgba(255,255,255,0.7)' },
              ].map(p => (
                <div key={p.label} style={{
                  padding: '5px 14px', borderRadius: 20,
                  background: 'rgba(255,255,255,0.13)', border: '1px solid rgba(255,255,255,0.22)',
                  fontSize: 12.5, fontWeight: 700, color: p.color,
                }}>
                  {p.label}
                </div>
              ))}
            </div>
          </div>

          {/* ── INFO BANNER ── */}
          <div style={{
            background: C.purpleL, border: `1px solid #C4B5FD`, borderRadius: 10,
            padding: '10px 16px', display:'flex', alignItems:'flex-start', gap:9,
          }}>
            <FiAlertCircle size={13} style={{ color:'#6D28D9', marginTop:1, flexShrink:0 }}/>
            <span style={{ fontSize:12.5, color:'#4C1D95', fontWeight:500, lineHeight:1.55 }}>
              <strong>Flow:</strong> After Admin approves PDD → if Invoice Required → enter details here →
              then fill Revenue Management. Leads with <em>Invoice Not Required</em> go directly to Revenue Management.
            </span>
          </div>

          {/* ── FILTER + SEARCH BAR ── */}
          <div style={{
            background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
            padding: '12px 16px', display:'flex', gap:10, alignItems:'center', flexWrap:'wrap',
            boxShadow: '0 1px 4px rgba(13,59,94,0.05)',
          }}>
            {/* Status tabs */}
            <div style={{ display:'flex', gap:6 }}>
              {[
                { val:'PENDING', label:'⏳ Pending' },
                { val:'ALL',     label:'All'        },
                { val:'DONE',    label:'✓ Done'     },
              ].map(tab => {
                const on = filter === tab.val;
                const tabColor = tab.val === 'PENDING' ? C.gold : tab.val === 'DONE' ? C.green : C.navy;
                return (
                  <button
                    key={tab.val}
                    onClick={() => onTabClick(tab.val)}
                    style={{
                      padding:'7px 14px', borderRadius:20, fontFamily:'inherit',
                      border: `1.5px solid ${on ? tabColor : C.border}`,
                      background: on ? tabColor : C.white,
                      color: on ? '#fff' : C.t2,
                      fontSize:12.5, fontWeight:700, cursor:'pointer', transition:'all 0.12s',
                    }}>
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Search */}
            <div style={{ flex:1, minWidth:180, position:'relative' }}>
              <FiSearch size={13} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/>
              <input
                type="text"
                placeholder="Search lead name, lead no..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') fetchData(1, filter, search); }}
                style={{
                  width:'100%', height:38, border:`1.5px solid ${C.border}`, borderRadius:9,
                  padding:'0 12px 0 30px', fontSize:13, color:C.t1, background:'#FAFBFC',
                  outline:'none', boxSizing:'border-box', fontFamily:'inherit',
                }}
              />
            </div>

            <button
              onClick={() => fetchData(page, filter, search)}
              title="Refresh"
              style={{
                width:38, height:38, borderRadius:9, background:C.navyL,
                border:`1px solid ${C.border}`, color:C.navy, cursor:'pointer',
                display:'flex', alignItems:'center', justifyContent:'center',
              }}>
              <FiRefreshCw size={13}/>
            </button>
          </div>

          {/* ── TABLE ── */}
          <div style={{
            background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
            overflow: 'hidden', boxShadow: '0 1px 4px rgba(13,59,94,0.05)',
          }}>

            {/* Loading shimmer */}
            {loading && (
              <div style={{ padding:16 }}>
                {[1,2,3,4,5].map(i => <Sh key={i}/>)}
              </div>
            )}

            {/* Empty state */}
            {!loading && records.length === 0 && (
              <div style={{ padding:'56px 24px', textAlign:'center' }}>
                <div style={{
                  width:54, height:54, borderRadius:'50%', background:C.purpleL,
                  display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px',
                }}>
                  <FiFileText size={22} style={{ color:'#6D28D9' }}/>
                </div>
                <div style={{ fontSize:15, fontWeight:700, color:C.t1, marginBottom:6 }}>
                  {filter === 'PENDING' ? 'No pending invoices 🎉' : 'No records found'}
                </div>
                <div style={{ fontSize:13, color:C.t4 }}>
                  {filter === 'PENDING'
                    ? 'All invoice details have been entered.'
                    : 'Invoice records appear here after PDD is approved and invoice is required.'}
                </div>
              </div>
            )}

            {/* Table */}
            {!loading && records.length > 0 && (
              <>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', minWidth:860 }}>
                    <thead>
                      <tr style={{ background:'#F7F9FB' }}>
                        {['Lead / Customer','Product','Payout to Co.','Invoice Number','Invoice Date','Invoice Amount','Status','Actions'].map((h, i) => (
                          <th key={h} style={{
                            padding:'10px 16px', fontSize:10.5, fontWeight:800,
                            color:C.t1, textTransform:'uppercase', letterSpacing:'0.07em',
                            textAlign: i === 7 ? 'right' : 'left',
                            borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap',
                          }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {records.map(rev => (
                        <InvoiceRow
                          key={rev.id}
                          revenue={rev}
                          lead={leadsMap[rev.lead] || null}
                          onSaved={() => fetchData(page, filter, search)}
                          navigate={navigate}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{
                    borderTop:`1px solid ${C.borderL}`, padding:'12px 16px',
                    display:'flex', justifyContent:'space-between', alignItems:'center',
                    background:'#FAFBFC',
                  }}>
                    <span style={{ fontSize:12.5, color:C.t4 }}>
                      Page {page} of {totalPages} · {total} records
                    </span>
                    <div style={{ display:'flex', gap:8 }}>
                      <button
                        onClick={() => fetchData(page - 1, filter, search)}
                        disabled={page <= 1}
                        style={{
                          padding:'6px 16px', borderRadius:8, border:`1px solid ${C.border}`,
                          background:C.white, color:page<=1?C.t4:C.navy,
                          fontSize:13, fontWeight:600, cursor:page<=1?'default':'pointer',
                          fontFamily:'inherit',
                        }}>
                        ← Prev
                      </button>
                      <button
                        onClick={() => fetchData(page + 1, filter, search)}
                        disabled={page >= totalPages}
                        style={{
                          padding:'6px 16px', borderRadius:8, border:`1px solid ${C.border}`,
                          background:C.white, color:page>=totalPages?C.t4:C.navy,
                          fontSize:13, fontWeight:600, cursor:page>=totalPages?'default':'pointer',
                          fontFamily:'inherit',
                        }}>
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

        </div>
      </Layout>
    </>
  );
}
